import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Eye, EyeOff } from 'lucide-react';
import SalonLandingBackground, {
  KIOSK_PAGE_KEYS,
} from '../../salon/components/SalonLandingBackground';
import '../../salon/components/SalonLandingBackground.css';
import {
  clearFranchiseFlowAuth,
  clearFranchiseFlowLastPath,
  hasFranchiseFlowAuth,
  loginFranchiseFlow,
  readFranchiseFlowLastPath,
  validateFranchiseFlowAuth,
  updateFranchiseFlowPassword,
} from '../../shared/franchiseFlowAuth';
import { isValidDevicePassword, DEVICE_PASSWORD_HINT } from '../../admin-shared/devicePassword';
import { clearAllGuestKioskState } from '../utils/faceIdentityApi';
import { prefetchDeviceLocation } from '../../shared/deviceLocationCache';
import {
  clearPresentationDemoGuestState,
  clearPresentationMode,
} from '../../presentation/presentationSession';
import {
  enforceFranchiseGeofence,
  FranchiseGeofenceError,
} from '../../shared/franchiseGeofence';
import FranchiseStoreLocationRequiredModal from '../../shared/FranchiseStoreLocationRequiredModal';
import PageFooter from '../../salon/components/PageFooter';
import LoginInfoTip from '../../shared/LoginInfoTip';
import {
  loadRememberedLogin,
  onRememberMeToggle,
  persistRememberedLogin,
} from '../../../shared/loginRememberMe';
import { resolvePostFranchiseLoginPath } from '../../shared/kioskLoginRedirect';
import { isFranchisePassportCheckinEnabled } from '../../shared/franchiseFlowAuth';
import { faceIdentityHomePath } from '../utils/faceIdentityRoutes';
import { resolveNyafazReelsBase } from '../../../nyafaz_reels/paths';
import '../styles/FaceIdentityLuxTheme.css';
import './FaceIdentityLoginPage.css';

const REMEMBER_PREFIX = {
  'face-identity': 'naturals_face_identity',
  passport: 'naturals_passport',
  salon: 'naturals_kiosk_salon',
  reels: 'naturals_reels',
};

function FiPasswordField({ label, value, onChange, placeholder, autoComplete }) {
  const [show, setShow] = useState(false);
  return (
    <label className="fi-login__field">
      <span>{label}</span>
      <div className="fi-login__input-wrap">
        <input
          type={show ? 'text' : 'password'}
          autoComplete={autoComplete}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          required
        />
        <button
          type="button"
          className="fi-login__password-toggle"
          onClick={() => setShow((prev) => !prev)}
          aria-label={show ? 'Hide password' : 'Show password'}
          aria-pressed={show}
          tabIndex={-1}
        >
          {show ? <EyeOff size={18} aria-hidden="true" /> : <Eye size={18} aria-hidden="true" />}
        </button>
      </div>
    </label>
  );
}

/**
 * Shared franchise device login for Face Identity and Salon analysis.
 * Enforces store GPS geofence (250 m) after credentials succeed.
 * @param {{ variant?: 'face-identity' | 'passport' | 'salon' | 'reels' }} props
 */
export default function FaceIdentityLoginPage({ variant = 'face-identity' }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [checking, setChecking] = useState(true);
  const [locationModalOpen, setLocationModalOpen] = useState(false);
  const [modalFranchiseName, setModalFranchiseName] = useState('');
  const [mustResetPassword, setMustResetPassword] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);

  const rememberPrefix = REMEMBER_PREFIX[variant] || REMEMBER_PREFIX['face-identity'];

  const isSalon = variant === 'salon';
  const isReels = variant === 'reels';
  const isPassport = variant === 'passport';
  const successPath = isReels
    ? (location.pathname.includes('/nyafaz-reels') ? resolveNyafazReelsBase() : '/naturals/reels')
    : isSalon
      ? '/naturals/salon'
      : isPassport
        ? faceIdentityHomePath('/naturals/passport')
        : '/naturals/face-identity';
  const title = isReels
    ? 'Reels Capture Login'
    : isSalon
      ? 'Salon Analysis Login'
      : isPassport
        ? 'Passport Login'
        : 'Face Identity Login';
  const subtitle = isReels
    ? 'Sign in with franchise device credentials to capture transformation reels. Access stays limited to within 250 m of the registered outlet.'
    : 'Sign in at the store with franchise device credentials. Access stays limited to within 250 m of the registered outlet — leaving the area signs you out automatically.';
  const sessionExpiredNotice = Boolean(location.state?.sessionExpired);
  const geofenceLogoutNotice = Boolean(location.state?.geofenceLogout);
  const geofenceLogoutMessage =
    (typeof location.state?.geofenceMessage === 'string' && location.state.geofenceMessage.trim()) ||
    'You left the store area. Sign in again within 250 m of the registered outlet.';

  const handleGeofenceFailure = (err) => {
    clearFranchiseFlowAuth();
    if (err instanceof FranchiseGeofenceError && err.code === 'STORE_LOCATION_REQUIRED') {
      setModalFranchiseName(err.extras?.franchise_name || '');
      setLocationModalOpen(true);
      setError('');
      return;
    }
    setError(err?.message || 'Location check failed');
  };

  const finishLoginWithGeofence = async (session) => {
    if (session?.must_reset_password) {
      setMustResetPassword(true);
      return;
    }
    await enforceFranchiseGeofence(session);
    if (isPassport) clearPresentationMode();
    if (isSalon) {
      clearPresentationMode();
      clearPresentationDemoGuestState();
    }
    if (location.state?.kioskLogout) {
      clearFranchiseFlowLastPath();
    }
    const from = location.state?.kioskLogout ? undefined : location.state?.from;
    const lastPath = location.state?.kioskLogout ? '' : readFranchiseFlowLastPath();
    const hub =
      isPassport && !isFranchisePassportCheckinEnabled(session)
        ? faceIdentityHomePath('/naturals/passport')
        : successPath;
    navigate(resolvePostFranchiseLoginPath(from, hub, lastPath), { replace: true });
  };

  const onPasswordReset = async (e) => {
    e.preventDefault();
    setError('');
    if (!isValidDevicePassword(newPassword)) {
      setError(DEVICE_PASSWORD_HINT);
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    setBusy(true);
    try {
      await updateFranchiseFlowPassword({
        currentPassword: password,
        newPassword,
      });
      const session = await validateFranchiseFlowAuth();
      setMustResetPassword(false);
      setNewPassword('');
      setConfirmPassword('');
      await enforceFranchiseGeofence(session);
      if (location.state?.kioskLogout) {
        clearFranchiseFlowLastPath();
      }
      const from = location.state?.kioskLogout ? undefined : location.state?.from;
      const lastPath = location.state?.kioskLogout ? '' : readFranchiseFlowLastPath();
      navigate(resolvePostFranchiseLoginPath(from, successPath, lastPath), { replace: true });
    } catch (err) {
      if (err instanceof FranchiseGeofenceError) {
        handleGeofenceFailure(err);
      } else {
        setError(err?.message || 'Password update failed');
      }
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    prefetchDeviceLocation();
  }, []);

  useEffect(() => {
    const { rememberMe: savedRemember, identifier } = loadRememberedLogin(rememberPrefix);
    setRememberMe(savedRemember);
    if (savedRemember && identifier) {
      setUsername(identifier);
    }
  }, [rememberPrefix]);

  // Fresh login screen — never keep the previous guest profile around.
  useEffect(() => {
    if (!hasFranchiseFlowAuth()) {
      clearAllGuestKioskState();
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!hasFranchiseFlowAuth()) {
        if (!cancelled) setChecking(false);
        return;
      }
      try {
        const ok = await validateFranchiseFlowAuth();
        if (cancelled) return;
        if (!ok) {
          clearAllGuestKioskState();
          setChecking(false);
          return;
        }
        await finishLoginWithGeofence(ok);
      } catch (err) {
        if (cancelled) return;
        handleGeofenceFailure(err);
        setChecking(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate, location.state, successPath]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLocationModalOpen(false);
    setBusy(true);
    try {
      clearAllGuestKioskState();
      const session = await loginFranchiseFlow({
        username: username.trim(),
        password,
      });
      persistRememberedLogin(rememberPrefix, rememberMe, username.trim());
      await finishLoginWithGeofence(session);
    } catch (err) {
      if (err instanceof FranchiseGeofenceError) {
        handleGeofenceFailure(err);
      } else {
        setError(err?.message || 'Sign in failed');
      }
    } finally {
      setBusy(false);
    }
  };

  if (checking) {
    return (
      <div className="fi-login fi-lux-theme">
        <SalonLandingBackground
          pageKey={isSalon ? KIOSK_PAGE_KEYS.SALON_ENTRY : KIOSK_PAGE_KEYS.FACE_IDENTITY}
        />
        <div className="fi-login__stage">
          <p className="fi-login__checking">Checking session and store location…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="fi-login fi-lux-theme">
      <SalonLandingBackground
        pageKey={isSalon ? KIOSK_PAGE_KEYS.SALON_ENTRY : KIOSK_PAGE_KEYS.FACE_IDENTITY}
      />
      <div className="fi-login__stage">
        <form className="fi-login__card" onSubmit={mustResetPassword ? onPasswordReset : onSubmit}>
          <h1 className="fi-login__title">
            <span>{mustResetPassword ? 'Set new device password' : title}</span>
            <LoginInfoTip
              label={mustResetPassword ? 'Password requirements' : 'Login information'}
              text={
                mustResetPassword
                  ? `Your device password must be changed before continuing. ${DEVICE_PASSWORD_HINT}`
                  : subtitle
              }
            />
          </h1>
          {!mustResetPassword ? (
            <>
          <label className="fi-login__field">
            <span>User ID</span>
            <input
              type="text"
              autoComplete="username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="Enter user ID"
              required
            />
          </label>
          <FiPasswordField
            label="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Enter password or PIN"
            autoComplete="current-password"
          />
          <label className="fi-login__remember">
            <input
              type="checkbox"
              checked={rememberMe}
              onChange={(e) => onRememberMeToggle(rememberPrefix, e.target.checked, setRememberMe)}
            />
            Remember me
          </label>
            </>
          ) : (
            <>
          <FiPasswordField
            label="New password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            placeholder="abcd1234"
            autoComplete="new-password"
          />
          <FiPasswordField
            label="Confirm password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            placeholder="Re-enter password"
            autoComplete="new-password"
          />
            </>
          )}
          {sessionExpiredNotice ? (
            <p className="fi-login__error" role="status">
              Session expired after 12 hours. Please sign in again.
            </p>
          ) : null}
          {geofenceLogoutNotice ? (
            <p className="fi-login__error" role="status">
              {geofenceLogoutMessage}
            </p>
          ) : null}
          {error ? <p className="fi-login__error" role="alert">{error}</p> : null}
          <button type="submit" className="fi-login__submit" disabled={busy}>
            {busy ? 'Please wait…' : mustResetPassword ? 'Update password' : 'Sign in'}
          </button>
        </form>
      </div>

      <PageFooter />

      <FranchiseStoreLocationRequiredModal
        open={locationModalOpen}
        franchiseName={modalFranchiseName}
        onClose={() => setLocationModalOpen(false)}
      />
    </div>
  );
}
